import posthog from 'posthog-js';
import { LABS_EXCEPTION_CAPTURE, scrubExceptionEvent } from '../../../shared/exception-scrubber';
import { posthogEventName, type AnalyticsEvent } from '../../core/events';

/**
 * The browser-side analytics adapter. Two rules, copied from packages/objectives:
 *
 * 1. No key, no vendor. CI, local dev and the browser tests run without one.
 * 2. A vendor cannot break the flow. Every call is wrapped.
 *
 * Session replay is on, so every element that renders writing or rubric text carries
 * `ph-mask` (inputs) or `ph-no-capture` (rendered text). The writing is a student's.
 */

export interface Analytics {
  track: (event: AnalyticsEvent) => void;
  ready: boolean;
}

const env = import.meta.env;

/** Rule 2 above, as a function: run it, and a throw is a warning, never a failure. */
export function swallow(label: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    console.warn(`[analytics] ${label} failed`, error);
  }
}

function initPostHog(userId?: string): ((event: AnalyticsEvent) => void) | null {
  const key = env.VITE_POSTHOG_KEY;
  if (!key) return null;

  posthog.init(key, {
    api_host: env.VITE_POSTHOG_HOST ?? 'https://s.240.org',
    ui_host: 'https://us.posthog.com',
    defaults: '2026-05-30',
    person_profiles: 'identified_only',
    capture_pageview: false,
    autocapture: false,
    disable_session_recording: false,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask',
      blockClass: 'ph-no-capture',
      blockSelector: '.cl-rootBox, .cl-userButtonPopoverCard',
      recordHeaders: false,
      recordBody: false,
      recordCrossOriginIframes: false,
      captureCanvas: { recordCanvas: false },
      maskCapturedNetworkRequestFn: (request) => ({ ...request, name: request.name.split(/[?#]/)[0]! }),
    },
    capture_pageleave: false,
    capture_dead_clicks: false,
    // Errors are on, scrubbed of writing first. See shared/exception-scrubber.ts.
    capture_exceptions: LABS_EXCEPTION_CAPTURE,
    before_send: scrubExceptionEvent,
    capture_heatmaps: false,
    capture_performance: false,
    disable_surveys: true,
  });
  if (userId) posthog.identify(userId);
  posthog.startSessionRecording();

  return (event) => swallow('posthog.capture', () =>
    posthog.capture(posthogEventName(event.name), { ...event.properties, product: 'preview_rubric', event_source: 'browser' }));
}

export function nullAnalytics(): Analytics {
  return { track: () => {}, ready: true };
}

export async function createAnalytics(userId?: string): Promise<Analytics> {
  let capture: ((event: AnalyticsEvent) => void) | null = null;
  swallow('posthog.init', () => {
    capture = initPostHog(userId);
  });
  return { ready: true, track: (event) => { capture?.(event); } };
}

/** Correlation only; the Worker still derives the user from the verified Clerk token. */
export function posthogHeaders(): Record<string, string> {
  if (!env.VITE_POSTHOG_KEY) return {};
  try {
    const distinctId = posthog.get_distinct_id();
    const sessionId = posthog.get_session_id();
    return distinctId && sessionId
      ? { 'X-POSTHOG-DISTINCT-ID': distinctId, 'X-POSTHOG-SESSION-ID': sessionId }
      : {};
  } catch { return {}; }
}

export function stopAnalytics(): void {
  if (!env.VITE_POSTHOG_KEY) return;
  swallow('stop', () => { posthog.stopSessionRecording(); posthog.reset(); });
}
